import { useEffect, useRef } from "react"
import { Modal, Platform, Pressable, TextInput, View } from "react-native"
import MIcon from "@react-native-vector-icons/material-design-icons"
import A, { useAnimatedStyle } from "react-native-reanimated"
import { useReanimatedKeyboardAnimation } from "react-native-keyboard-controller"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { createWeightDefinedInput } from "@white-labels/input"

import { Text } from "@src/components/core"
import { ar } from "@src/i18n/ar"
import { trpc } from "@src/utils/trpc"

interface Props {
  baseProductId: number
  /** If provided, opens in edit mode for this weight-defined product */
  productId?: number
  /** Current weight value (for edit mode pre-fill) */
  initialWeight?: number
  /** Current status (for edit mode pre-fill) */
  initialStatus?: "active" | "inactive"
  onClose: () => void
}

export function WeightDefinedProductForm({ baseProductId, productId, initialWeight, initialStatus, onClose }: Props) {
  const utils = trpc.useUtils()
  const createWeightDefined = trpc.scaleProducts.createWeightDefined.useMutation()
  const editWeightDefined = trpc.scaleProducts.editWeightDefined.useMutation()

  const isEditMode = !!productId
  const weightRef = useRef<TextInput | null>(null)

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(createWeightDefinedInput),
    defaultValues: {
      baseProductId,
      weight: initialWeight ?? 0,
      status: initialStatus ?? "active",
    },
  })

  const { height, progress } = useReanimatedKeyboardAnimation()
  const containerStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: height.value * progress.value * 0.3 }],
    }
  }, [])

  const loading = createWeightDefined.isPending || editWeightDefined.isPending
  const disabled = !formState.isValid || loading

  const onSubmit = handleSubmit((data) => {
    if (isEditMode && productId) {
      editWeightDefined.mutate(
        { id: productId, weight: data.weight, status: data.status },
        {
          onSuccess: () => {
            utils.products.get.invalidate({ id: baseProductId })
            utils.products.get.invalidate({ id: productId })
            utils.products.list.invalidate()
            onClose()
          },
          onError: (error) => {
            alert(error.message || ar.somethingWentWrong)
          },
        },
      )
    } else {
      createWeightDefined.mutate(
        { baseProductId, weight: data.weight, status: data.status },
        {
          onSuccess: () => {
            utils.products.get.invalidate({ id: baseProductId })
            utils.products.list.invalidate()
            onClose()
          },
          onError: (error) => {
            alert(error.message || ar.somethingWentWrong)
          },
        },
      )
    }
  })

  // Auto-focus on open
  useEffect(() => {
    requestAnimationFrame(() => {
      weightRef.current?.focus()
    })
  }, [])

  // Close on Escape (web)
  useEffect(() => {
    if (Platform.OS === "web") {
      const handler = (e: KeyboardEvent) => {
        if (e.key === "Escape") onClose()
      }
      window.addEventListener("keydown", handler)
      return () => window.removeEventListener("keydown", handler)
    }
  }, [onClose])

  return (
    <Modal transparent animationType="fade" onRequestClose={onClose} visible>
      <Pressable className="absolute top-0 left-0 right-0 bottom-0 bg-black/30" onPress={onClose} />
      <A.View className="bg-white mt-auto mb-auto mx-2 px-4 py-4 max-w-lg w-full xs:mx-auto rounded" style={containerStyle}>
        {/* Header */}
        <View className="flex-row items-center justify-between mb-4">
          <Text className="text-lg font-extrabold text-gray-900 tracking-tight">{isEditMode ? ar.editWeightDefinedProduct : ar.addWeightDefinedProduct}</Text>
          <Pressable onPress={onClose} className="p-2">
            <MIcon name="close" size={22} color="#6B7280" />
          </Pressable>
        </View>

        {/* Weight Input */}
        <Text className="text-base font-bold text-gray-900 mb-2 text-right">
          {ar.weight} ({ar.gms})
        </Text>
        <Controller
          control={control}
          name="weight"
          render={({ field }) => (
            <View className="relative mb-4">
              <TextInput
                ref={weightRef}
                className="h-[46] px-4 border bg-white border-gray-300 focus:border-purple-600 rounded text-right text-lg"
                placeholder="0"
                placeholderTextColor="#9CA3AF"
                keyboardType="numeric"
                textAlign="right"
                selectTextOnFocus
                value={field.value ? field.value.toString() : ""}
                onChangeText={(text) => {
                  if (/^\d*$/.test(text)) {
                    field.onChange(text === "" ? 0 : parseInt(text, 10))
                  }
                }}
              />
              <View className="absolute left-4 top-1/2 -translate-y-1/2">
                <Text className="text-gray-500 font-medium text-sm">{ar.gms}</Text>
              </View>
            </View>
          )}
        />

        {/* Active Status Toggle */}
        <Controller
          control={control}
          name="status"
          render={({ field }) => (
            <Pressable className="flex-row items-center justify-between mb-4 py-2" onPress={() => field.onChange(field.value === "active" ? "inactive" : "active")}>
              <Text className="text-base font-bold text-gray-900">{ar.active}</Text>
              <View className={`w-12 h-7 rounded-full justify-center px-0.5 ${field.value === "active" ? "bg-purple-600" : "bg-gray-300"}`}>
                <View className={`w-6 h-6 rounded-full bg-white shadow ${field.value === "active" ? "self-start" : "self-end"}`} />
              </View>
            </Pressable>
          )}
        />

        {/* Submit Button */}
        <Pressable
          className={`h-12 rounded items-center justify-center active:opacity-90 disabled:opacity-50 shadow-sm ${disabled ? "bg-gray-400" : "bg-purple-600"}`}
          onPress={onSubmit}
          disabled={disabled}
        >
          <Text className="text-white font-bold text-lg tracking-wide">{loading ? ar.processing : isEditMode ? ar.save : ar.add}</Text>
        </Pressable>
      </A.View>
    </Modal>
  )
}
